<template>
  <div class="protection-shell">
    <div class="protection-grid">
      <section class="protection-card protection-card--control">
        <span class="protection-card__eyebrow">Proteccion</span>
        <h2>Fomo Safe por evento</h2>
        <p>
          Activa o desactiva esta proteccion para mostrarla en el checkout de este evento. Cubre imprevistos personales validables y cancelacion oficial del evento.
        </p>

        <div class="protection-toggle" :class="{ 'is-active': localEnabled }">
          <div>
            <strong>{{ localEnabled ? 'Activo' : 'Inactivo' }}</strong>
             <small>{{ localEnabled ? 'El checkout mostrara Fomo Safe cuando existan tickets elegibles.' : 'Los compradores pasaran directo al pago.' }}</small>
          </div>

          <button class="protection-toggle__button" type="button" :disabled="saving" @click="toggleProtection">
            {{ saving ? 'Guardando...' : localEnabled ? 'Desactivar' : 'Activar' }}
          </button>
        </div>

        <div class="protection-pricing">
          <strong>Escala actual</strong>
          <div class="protection-pricing__bands">
            <span>Hasta $30 -> $1.99</span>
            <span>Hasta $60 -> $2.99</span>
            <span>Hasta $100 -> $4.99</span>
            <span>Mas de $100 -> $7.99</span>
          </div>
        </div>

        <p v-if="feedback" class="protection-card__feedback">{{ feedback }}</p>
        <p v-if="errorMessage" class="protection-card__error">{{ errorMessage }}</p>
      </section>

      <section v-if="canViewInternalFinance" class="protection-card protection-card--metrics">
        <span class="protection-card__eyebrow">Resumen</span>
        <h2>Bruto y neto de Fomo Safe</h2>
        <p>
          Aqui ves el ingreso bruto de addons y el neto despues del costo de procesamiento asignado a Fomo Safe.
        </p>

        <div class="protection-metric-grid">
          <article class="protection-metric">
            <small>Fomo Safe activo</small>
            <strong>{{ localEnabled ? 'Si' : 'No' }}</strong>
          </article>
          <article class="protection-metric">
            <small>Addons vendidos</small>
            <strong>{{ event.total_addons_units || 0 }}</strong>
          </article>
          <article class="protection-metric">
            <small>Bruto addons</small>
            <strong>${{ formatMoney(event.total_addons_gross) }}</strong>
          </article>
          <article class="protection-metric">
            <small>Neto addons</small>
            <strong>${{ formatMoney(event.total_addons_net) }}</strong>
          </article>
          <article class="protection-metric">
            <small>En espera</small>
            <strong>{{ event.fomo_safe_generated_units || 0 }}</strong>
            <span>${{ formatMoney(event.fomo_safe_generated_gross) }}</span>
          </article>
          <article class="protection-metric">
            <small>En carrito</small>
            <strong>{{ event.fomo_safe_cart_units || 0 }}</strong>
            <span>${{ formatMoney(event.fomo_safe_cart_gross) }}</span>
          </article>
        </div>

        <div class="protection-note">
            <strong>Como leerlo</strong>
            <p>
            El bruto refleja lo cobrado por addons. El neto descuenta la parte del procesamiento asignada a Fomo Safe para que tengas una lectura mas real.
            </p>
            <p>
            `En espera` muestra addons en invoices generadas todavia no aprobadas. `En carrito` muestra interes creado pero aun no pagado.
            </p>
        </div>
      </section>

      <section v-else class="protection-card protection-card--metrics">
        <span class="protection-card__eyebrow">Resumen</span>
        <h2>Estado de proteccion</h2>
        <p>
          Puedes activar o desactivar Fomo Safe para tu evento, pero la lectura financiera del addon queda solo en el admin interno de FOMO.
        </p>

        <div class="protection-metric-grid">
          <article class="protection-metric">
            <small>Fomo Safe activo</small>
            <strong>{{ localEnabled ? 'Si' : 'No' }}</strong>
          </article>
          <article class="protection-metric">
            <small>Checkout</small>
            <strong>{{ localEnabled ? 'Con proteccion' : 'Sin proteccion' }}</strong>
          </article>
        </div>

        <div class="protection-note">
          <strong>Que ve tu equipo</strong>
          <p>
            En este panel de organizador solo dejas lista la proteccion. Los montos de Fomo Safe, netos o fees internos no se muestran aqui.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { apiService } from '../../common/api.services.js'

export default {
  name: 'EventProtectionView',

  props: {
    event: {
      type: Object,
      required: true,
    },
    slug: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      localEnabled: false,
      saving: false,
      feedback: '',
      errorMessage: '',
    }
  },

  watch: {
    event: {
      immediate: true,
      deep: true,
      handler(value) {
        this.localEnabled = Boolean(value && value.fomo_safe_enabled)
      },
    },
  },

  methods: {
    formatMoney(value) {
      const parsed = Number.parseFloat(value)
      if (Number.isNaN(parsed)) {
        return '0.00'
      }

      return parsed.toFixed(2)
    },

    toggleProtection() {
      this.saving = true
      this.feedback = ''
      this.errorMessage = ''

      apiService(`/api/events-fomo-safe/${this.slug}/`, 'PATCH', {
        fomo_safe_enabled: !this.localEnabled,
      }).then((data) => {
        if (data && !data.err) {
          this.localEnabled = Boolean(data.fomo_safe_enabled)
          this.feedback = this.localEnabled ? 'Fomo Safe quedo activo para este evento.' : 'Fomo Safe quedo desactivado para este evento.'
          this.$emit('event-updated', data)
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo actualizar la configuracion.'
      }).catch(() => {
        this.errorMessage = 'No se pudo actualizar la configuracion.'
      }).finally(() => {
        this.saving = false
      })
    },
  },

  computed: {
    canViewInternalFinance() {
      return Boolean(this.event && this.event.can_view_internal_finance)
    },
  },
}
</script>

<style scoped>
.protection-shell {
  padding-top: 20px;
}

.protection-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.protection-card {
  padding: 22px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(23, 27, 38, 0.96), rgba(11, 13, 19, 0.96));
  border: 1px solid rgba(194, 203, 219, 0.1);
  color: #eef2f6;
}

.protection-card__eyebrow {
  display: inline-flex;
  margin-bottom: 10px;
  padding: 7px 11px;
  border-radius: 999px;
  background: rgba(91, 209, 165, 0.12);
  border: 1px solid rgba(91, 209, 165, 0.18);
  color: #c9ffe7;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.protection-card h2,
.protection-note strong,
.protection-pricing strong {
  margin: 0;
}

.protection-card p,
.protection-note p {
  color: #9ca8bc;
  line-height: 1.55;
}

.protection-toggle,
.protection-pricing,
.protection-note {
  margin-top: 18px;
  padding: 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(194, 203, 219, 0.08);
}

.protection-toggle {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
}

.protection-toggle.is-active {
  border-color: rgba(91, 209, 165, 0.26);
  box-shadow: 0 0 0 1px rgba(91, 209, 165, 0.08);
}

.protection-toggle strong,
.protection-metric strong {
  display: block;
  color: #f5f7fb;
}

.protection-toggle small,
.protection-metric small {
  color: #9ca8bc;
}

.protection-metric span {
  display: block;
  margin-top: 6px;
  color: #b9c4d8;
  line-height: 1.35;
}

.protection-toggle__button {
  min-width: 132px;
  min-height: 46px;
  padding: 0 16px;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(135deg, #61d6b2, #8ef2ec);
  color: #051119;
  font-weight: 800;
}

.protection-pricing__bands,
.protection-metric-grid {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.protection-pricing__bands {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.protection-pricing__bands span,
.protection-metric {
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(194, 203, 219, 0.08);
}

.protection-metric-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.protection-metric strong {
  margin-top: 8px;
  font-size: 1.4rem;
  line-height: 1;
}

.protection-card__feedback {
  margin-top: 16px;
  color: #baf5e4;
}

.protection-card__error {
  margin-top: 16px;
  color: #ffb7c3;
}

@media (max-width: 900px) {
  .protection-grid,
  .protection-metric-grid,
  .protection-pricing__bands {
    grid-template-columns: 1fr;
  }

  .protection-toggle {
    flex-direction: column;
    align-items: flex-start;
  }

  .protection-toggle__button {
    width: 100%;
  }
}
</style>
